import type { CSSProperties, ReactNode } from 'react'

/**
 * The dashboard chart kit — every one of them drawn from a payload the screen
 * already fetched.
 *
 * 🔴 THIS WAS `school/SchoolCharts.tsx` AND IT MOVED UP WHEN THE SECOND AND
 * THIRD TIERS ASKED FOR THE SAME BARS. The teacher's priority panels and the
 * parent's skill rows had each grown their own hand-drawn bar — different
 * heights, different tracks, one of them a raw `bg-slate-100` that is not a
 * token at all — while this file sat one folder away with the argument for why
 * a bar must print its own fraction. Three bars that can disagree about what a
 * quiet class looks like is exactly the drift `architecture.md` §12 names.
 *
 * ⚠️ `school/SchoolCharts.tsx` STILL EXISTS AND RE-EXPORTS FROM HERE, so the
 * school screens' imports are untouched and the move is invisible to them.
 *
 * 🔴 THE NUMBER IS ON SCREEN AS A NUMBER, ALWAYS. Every chart here prints its
 * counts in text beside the bar, and the bar is decoration. That is not a
 * stylistic preference: a head teacher reads these out at a governors' meeting
 * and puts them in a letter, and a width cannot be read aloud. It is also what
 * keeps OD-39/OD-72 true in a picture — *"a count travels with its
 * denominator, never a percentage"* survives being drawn only if the pair is
 * still written down.
 *
 * 🔴 SVG AND TOKENS, NO LIBRARY AND NO HEX. There is no charting dependency in
 * this product and these shapes do not justify adding one — the whole kit is
 * four primitives over `<div>` widths and one `<svg>`. Colour comes from
 * `--color-*` so a token change reaches the charts, which is what
 * `design-system.md` §1 asks and what the platform tier's hand-picked pastels
 * failed.
 *
 * ⚠️ THE GRAPHIC IS `aria-hidden` AND THE TEXT IS NOT. A screen reader gets the
 * label and the fraction; it does not get told there is a rectangle.
 */

export type ChartTone = 'primary' | 'leaf' | 'teal' | 'indigo' | 'sunshine' | 'coral'

const TONE: Record<ChartTone, string> = {
  primary: 'var(--color-primary)',
  leaf: 'var(--color-leaf)',
  teal: 'var(--color-teal)',
  indigo: 'var(--color-indigo)',
  sunshine: 'var(--color-sunshine)',
  coral: 'var(--color-coral)',
}

/** The warm neutral every empty track in the product uses — `CoverageRing`'s. */
const TRACK = 'var(--color-pill-neutral-bg)'

/**
 * The panel every chart sits in.
 *
 * ⚠️ ONE CHROME, DEFINED ONCE. Six charts each writing their own heading, hint
 * and card is six chances for the seventh to look like a different product —
 * the failure `DashUI`'s own header describes about tiles.
 */
export function ChartPanel({
  title,
  hint,
  action,
  children,
}: {
  title: ReactNode
  hint?: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-card sm:p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-dash text-dash-md font-extrabold text-ink">{title}</h3>
          {hint !== undefined && (
            <p className="mt-0.5 font-dash text-dash-xs text-muted">{hint}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  )
}

export interface ShareRow {
  key: string
  label: ReactNode
  count: number
  /** The denominator. Zero draws an empty track rather than dividing by it. */
  total: number
  tone?: ChartTone
}

/**
 * Horizontal bars, each a count over its own denominator.
 *
 * 🔴 `total === 0` IS AN EMPTY TRACK, NOT A ZERO PERCENT. A class with no
 * children on it is not disengaged; it has no share to be at, and drawing it at
 * the far left says the opposite of the truth about it.
 */
export function ShareBars({ rows, emptyMessage }: { rows: readonly ShareRow[]; emptyMessage: ReactNode }) {
  if (rows.length === 0) {
    return <p className="font-dash text-dash-xs font-semibold text-muted">{emptyMessage}</p>
  }

  return (
    <ul className="flex flex-col gap-3.5">
      {rows.map((row, i) => {
        const share = row.total > 0 ? Math.min(1, row.count / row.total) : 0
        const fill = TONE[row.tone ?? 'primary']
        return (
          <li key={row.key} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate font-dash text-dash-xs font-bold text-ink">
                {row.label}
              </span>
              <span className="tabular shrink-0 font-dash text-dash-xs font-extrabold text-ink">
                {row.count} / {row.total}
              </span>
            </div>
            <div
              aria-hidden="true"
              className="h-2.5 w-full overflow-hidden rounded-full"
              style={{ background: TRACK }}
            >
              <div
                className="play-rise h-full rounded-full"
                style={
                  {
                    width: `${String(share * 100)}%`,
                    background: fill,
                    '--rise-i': i,
                  } as CSSProperties
                }
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export interface ColumnRow {
  key: string
  label: ReactNode
  value: number
  tone?: ChartTone
}

/**
 * Vertical columns for an absolute count — sittings, rounds, events.
 *
 * 🔴 SCALED TO THE LARGEST COLUMN, AND THAT IS SAID ON SCREEN. A column chart
 * with no axis is a shape; the tallest value is printed above the plot so the
 * reader knows what full height means. Scaling to a round number instead would
 * be a second, invented figure.
 *
 * ⚠️ ALL-ZERO DATA DRAWS A FLAT ROW OF TRACKS RATHER THAN FIVE FULL COLUMNS.
 * `max` of nothing is what turns "nobody played" into "everybody played".
 */
export function Columns({ rows, emptyMessage }: { rows: readonly ColumnRow[]; emptyMessage: ReactNode }) {
  if (rows.length === 0) {
    return <p className="font-dash text-dash-xs font-semibold text-muted">{emptyMessage}</p>
  }
  const max = Math.max(...rows.map((r) => r.value))

  return (
    <div className="flex items-end gap-2 sm:gap-3" style={{ minHeight: 140 }}>
      {rows.map((row, i) => {
        const share = max > 0 ? row.value / max : 0
        return (
          <div key={row.key} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
            <span className="tabular font-dash text-dash-xs font-extrabold text-ink">
              {row.value}
            </span>
            <div
              aria-hidden="true"
              className="flex w-full items-end rounded-t-md"
              style={{ height: 96, background: TRACK }}
            >
              <div
                className="play-rise w-full rounded-t-md"
                style={
                  {
                    height: `${String(Math.max(share * 100, row.value > 0 ? 4 : 0))}%`,
                    background: TONE[row.tone ?? 'indigo'],
                    '--rise-i': i,
                  } as CSSProperties
                }
              />
            </div>
            <span className="w-full truncate text-center font-dash text-[11px] font-bold text-muted">
              {row.label}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export interface SplitRow {
  key: string
  label: ReactNode
  /** Left-hand part — the one being celebrated. */
  first: number
  second: number
  firstLabel: string
  secondLabel: string
  /** Whatever the server says the pair comes to. Never recomputed here. */
  trailing?: ReactNode
}

/**
 * A two-part bar — right answers against wrong ones.
 *
 * 🔴 THE PERCENTAGE IS THE SERVER'S OR IT IS ABSENT. `GameStat.accuracyPct` is
 * computed once, server-side, over the set the server scoped; a second division
 * in the browser is the *"two implementations that agree until one moves"*
 * this codebase keeps removing. So `trailing` is passed in, never derived.
 */
export function SplitBars({ rows, emptyMessage }: { rows: readonly SplitRow[]; emptyMessage: ReactNode }) {
  if (rows.length === 0) {
    return <p className="font-dash text-dash-xs font-semibold text-muted">{emptyMessage}</p>
  }

  return (
    <ul className="flex flex-col gap-3.5">
      {rows.map((row, i) => {
        const whole = row.first + row.second
        const firstShare = whole > 0 ? (row.first / whole) * 100 : 0
        return (
          <li key={row.key} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate font-dash text-dash-xs font-bold text-ink">
                {row.label}
              </span>
              <span className="tabular shrink-0 font-dash text-dash-xs font-bold text-muted">
                {row.first} {row.firstLabel} · {row.second} {row.secondLabel}
                {row.trailing !== undefined && (
                  <>
                    {' · '}
                    <span className="font-extrabold text-ink">{row.trailing}</span>
                  </>
                )}
              </span>
            </div>
            <div
              aria-hidden="true"
              className="flex h-2.5 w-full overflow-hidden rounded-full"
              style={{ background: TRACK }}
            >
              <div
                className="play-rise h-full"
                style={
                  {
                    width: `${String(firstShare)}%`,
                    background: TONE.leaf,
                    '--rise-i': i,
                  } as CSSProperties
                }
              />
              <div
                className="h-full flex-1"
                style={{ background: whole > 0 ? TONE.coral : 'transparent' }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export interface SliceRow {
  key: string
  label: ReactNode
  value: number
  tone: ChartTone
}

/**
 * A donut, for a whole that genuinely divides into parts.
 *
 * ⚠️ ONLY FOR A REAL PARTITION. A donut claims the slices add to the whole, so
 * it is used here for children grouped by the age band of their class — every
 * child in exactly one band — and never for a set of overlapping counts. The
 * platform tier's donuts were four hand-picked arc lengths over a total nobody
 * had added up, which is the failure this note exists to prevent repeating.
 */
export function Donut({
  slices,
  total,
  centreLabel,
  emptyMessage,
}: {
  slices: readonly SliceRow[]
  total: number
  centreLabel: ReactNode
  emptyMessage: ReactNode
}) {
  const drawable = slices.filter((s) => s.value > 0)
  if (total <= 0 || drawable.length === 0) {
    return <p className="font-dash text-dash-xs font-semibold text-muted">{emptyMessage}</p>
  }

  let offset = 0
  const arcs = drawable.map((slice) => {
    const length = (slice.value / total) * 100
    const arc = { slice, length, offset }
    offset += length
    return arc
  })

  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="relative shrink-0" style={{ width: 132, height: 132 }}>
        <svg viewBox="0 0 120 120" width="132" height="132" aria-hidden="true">
          <circle cx="60" cy="60" r="52" fill="none" stroke={TRACK} strokeWidth="12" />
          {arcs.map(({ slice, length, offset: at }) => (
            <circle
              key={slice.key}
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke={TONE[slice.tone]}
              strokeWidth="12"
              pathLength={100}
              strokeDasharray={`${String(length)} ${String(100 - length)}`}
              strokeDashoffset={String(-at)}
              transform="rotate(-90 60 60)"
            />
          ))}
        </svg>
        <span className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="tabular text-dash-xl leading-none font-extrabold text-ink">{total}</span>
          <span className="text-dash-xs font-bold text-muted">{centreLabel}</span>
        </span>
      </div>

      <ul className="flex min-w-0 flex-1 flex-col gap-2">
        {slices.map((slice) => (
          <li key={slice.key} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 font-dash text-dash-xs font-bold text-ink">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: TONE[slice.tone] }}
              />
              <span className="truncate">{slice.label}</span>
            </span>
            <span className="tabular shrink-0 font-dash text-dash-xs font-extrabold text-ink">
              {slice.value} / {total}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
